import axios from 'axios';

class NocodeBackendService {
  constructor() {
    this.apiKey  = import.meta.env.VITE_NOCODEBACKEND_API_KEY;
    this.baseURL = 'https://openapi.nocodebackend.com';
    this.instance = import.meta.env.VITE_NOCODEBACKEND_INSTANCE || '54230_nocodebackend';

    if (!this.apiKey) {
      console.error('[NocodeBackend] Missing VITE_NOCODEBACKEND_API_KEY');
      throw new Error('Missing VITE_NOCODEBACKEND_API_KEY. Set it in your environment.');
    }

    if (!this.instance) {
      console.error('[NocodeBackend] Missing VITE_NOCODEBACKEND_INSTANCE');
      throw new Error('Missing VITE_NOCODEBACKEND_INSTANCE. Set it in your environment.');
    }

    this.axios = axios.create({
      baseURL: this.baseURL,
      headers: {
        Authorization: `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    // IMPORTANT: keep any existing interceptors as-is (request/response logging, error handling, etc.)
    // Add request interceptor for debugging
    this.axios.interceptors.request.use(
      (config) => {
        console.log('🌐 [NocodeBackend] Request interceptor:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          fullURL: this.baseURL + config.url,
          headers: {
            'Content-Type': config.headers['Content-Type'],
            'Authorization': config.headers['Authorization'] ? `Bearer ${config.headers['Authorization'].substring(0, 10)}...` : 'None'
          },
          data: config.data
        });
        return config;
      },
      (error) => {
        console.error('❌ [NocodeBackend] Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for debugging
    this.axios.interceptors.response.use(
      (response) => {
        console.log('📥 [NocodeBackend] Response interceptor:', {
          status: response.status,
          statusText: response.statusText,
          config: {
            method: response.config.method?.toUpperCase(),
            url: response.config.url
          },
          data: response.data,
          headers: response.headers
        });
        return response;
      },
      (error) => {
        console.error('❌ [NocodeBackend] Response interceptor error:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url
          },
          headers: error.response?.headers
        });
        return Promise.reject(error);
      }
    );
  }

  async read(table, filters = {}) {
    try {
      const search = new URLSearchParams(filters).toString();
      const url = `/read/${table}?Instance=${this.instance}${search ? `&${search}` : ''}`;

      const response = await this.axios.get(url);
      const body = response.data;

      if (Array.isArray(body?.data)) return body.data;
      if (Array.isArray(body)) return body;
      return body; // fall back to raw body for flexibility
    } catch (error) {
      console.error('[NocodeBackend] Read error', { table, filters, error });
      throw error;
    }
  }

  async create(table, data) {
    try {
      const url = `/create/${table}?Instance=${this.instance}`;
      const response = await this.axios.post(url, data);
      const body = response.data;

      // Always standardize to { data: <row|rows> }
      return { data: body?.data ?? body };
    } catch (error) {
      console.error('[NocodeBackend] Create error', { table, data, error });
      throw error;
    }
  }

  async readById(table, id) {
    console.log('🔍 [NocodeBackend] Reading by ID from table:', table, 'ID:', id);
    try {
      const response = await this.axios.get(`/read/${table}/${id}?Instance=${this.instance}`);
      console.log('✅ [NocodeBackend] Read by ID successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [NocodeBackend] Read by ID error:', error);
      if (error.response) {
        console.error('🌐 [NocodeBackend] Response:', error.response.data);
      }
      throw new Error(`NocodeBackend read by ID error: ${error.response?.data?.message || error.message}`);
    }
  }

  async search(table, data) {
    console.log('🔍 [NocodeBackend] Searching table:', table, 'with data:', data);
    try {
      const response = await this.axios.post(`/search/${table}?Instance=${this.instance}`, data);
      console.log('✅ [NocodeBackend] Search successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [NocodeBackend] Search error:', error);
      if (error.response) {
        console.error('🌐 [NocodeBackend] Response:', error.response.data);
      }
      throw new Error(`NocodeBackend search error: ${error.response?.data?.message || error.message}`);
    }
  }

  async update(table, id, data) {
    console.log('🔄 [NocodeBackend]=====UPDATE START=====');
    console.log('📋 [NocodeBackend] Table:', table);
    console.log('🆔 [NocodeBackend] ID:', id);
    console.log('📤 [NocodeBackend] Data:', JSON.stringify(data, null, 2));
    
    try {
      const url = `/update/${table}/${id}?Instance=${this.instance}`;
      console.log('🌐 [NocodeBackend] Request URL:', this.baseURL + url);
      
      const response = await this.axios.put(url, data);
      console.log('📥 [NocodeBackend] Response status:', response.status);
      console.log('📥 [NocodeBackend] Response data:', JSON.stringify(response.data, null, 2));
      console.log('✅ [NocodeBackend]=====UPDATE SUCCESS=====');
      
      return response.data;
    } catch (error) {
      console.error('❌ [NocodeBackend]=====UPDATE FAILED=====');
      console.error('💥 [NocodeBackend] Error:', error);
      console.error('📋 [NocodeBackend] Table:', table);
      console.error('🆔 [NocodeBackend] ID:', id);
      console.error('📤 [NocodeBackend] Data:', data);
      
      if (error.response) {
        console.error('🌐 [NocodeBackend] Response status:', error.response.status);
        console.error('🌐 [NocodeBackend] Response data:', error.response.data);
        console.error('🌐 [NocodeBackend] Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('🌐 [NocodeBackend] No response received:', error.request);
      } else {
        console.error('🌐 [NocodeBackend] Request setup error:', error.message);
      }
      
      console.error('🔗 [NocodeBackend] Error stack:', error.stack);
      
      let errorMessage = `Update error: ${error.message}`;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      throw new Error(`NocodeBackend update error: ${errorMessage}`);
    }
  }

  async delete(table, id) {
    console.log('🗑️ [NocodeBackend]=====DELETE START=====');
    console.log('📋 [NocodeBackend] Table:', table);
    console.log('🆔 [NocodeBackend] ID:', id);
    
    try {
      const url = `/delete/${table}/${id}?Instance=${this.instance}`;
      console.log('🌐 [NocodeBackend] Request URL:', this.baseURL + url);
      
      const response = await this.axios.delete(url);
      console.log('📥 [NocodeBackend] Response status:', response.status);
      console.log('📥 [NocodeBackend] Response data:', JSON.stringify(response.data, null, 2));
      console.log('✅ [NocodeBackend]=====DELETE SUCCESS=====');
      
      return response.data;
    } catch (error) {
      console.error('❌ [NocodeBackend]=====DELETE FAILED=====');
      console.error('💥 [NocodeBackend] Error:', error);
      console.error('📋 [NocodeBackend] Table:', table);
      console.error('🆔 [NocodeBackend] ID:', id);
      
      if (error.response) {
        console.error('🌐 [NocodeBackend] Response status:', error.response.status);
        console.error('🌐 [NocodeBackend] Response data:', error.response.data);
        console.error('🌐 [NocodeBackend] Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('🌐 [NocodeBackend] No response received:', error.request);
      } else {
        console.error('🌐 [NocodeBackend] Request setup error:', error.message);
      }
      
      console.error('🔗 [NocodeBackend] Error stack:', error.stack);
      
      let errorMessage = `Delete error: ${error.message}`;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      throw new Error(`NocodeBackend delete error: ${errorMessage}`);
    }
  }

  // Authentication methods (if needed in the future)
  async authenticate(credentials) {
    console.log('🔐 [NocodeBackend] Authenticating:', credentials);
    try {
      const response = await this.axios.post('/auth/login', credentials);
      console.log('✅ [NocodeBackend] Authentication successful');
      return response.data;
    } catch (error) {
      console.error('❌ [NocodeBackend] Authentication error:', error);
      throw new Error(`Authentication error: ${error.response?.data?.message || error.message}`);
    }
  }

  async validateToken(token) {
    console.log('🔐 [NocodeBackend] Validating token...');
    try {
      const response = await this.axios.get('/auth/validate', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ [NocodeBackend] Token validation successful');
      return response.data;
    } catch (error) {
      console.error('❌ [NocodeBackend] Token validation error:', error);
      throw new Error(`Token validation error: ${error.response?.data?.message || error.message}`);
    }
  }

  // Test connection method
  async testConnection() {
    console.log('🧪 [NocodeBackend] Testing connection...');
    try {
      const response = await this.axios.get(`/read/contacts?Instance=${this.instance}`);
      console.log('✅ [NocodeBackend] Connection test successful:', response.status);
      return { success: true, status: response.status, data: response.data };
    } catch (error) {
      console.error('❌ [NocodeBackend] Connection test failed:', error);
      return { 
        success: false, 
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  }
}

export const nocodeBackendService = new NocodeBackendService();